import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { TrendingUp, Wallet, Clock, Network, ArrowRight } from 'lucide-react';
import { Button, Card, CardContent, Skeleton } from '@mlm/ui';
import { getEarningsBreakdown, getLedger, getMe, getSlotWallet, getSlotStanding } from '../lib/api';
import { useMySlots } from '../lib/hooks/use-my-slots';
import { StatementList } from '../components/wallet/statement-list';
import { EarningsBreakdownPanel } from '../components/wallet/earnings-breakdown-panel';
import { SegmentedToggle } from '../components/segmented-toggle';
import { KpiCard } from '../components/kpi-card';
import { fmtCurrency } from '../lib/format';

const PAGE_SIZE = 50;

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'C', label: 'Credits' },
  { value: 'D', label: 'Debits' },
] as const;

export function EarningsPage() {
  const { activeSlotId, activeSlotLabel } = useMySlots();
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<'all' | 'C' | 'D'>('all');
  const meQ = useQuery({ queryKey: ['me'], queryFn: getMe });

  const walletQ = useQuery({
    queryKey: ['wallet', activeSlotId],
    queryFn: () => getSlotWallet(activeSlotId ?? null),
    enabled: !!activeSlotId,
  });
  const standingQ = useQuery({
    queryKey: ['standing', activeSlotId],
    queryFn: () => getSlotStanding(activeSlotId!),
    enabled: !!activeSlotId,
  });
  const earningsBreakdownQ = useQuery({
    queryKey: ['earnings-breakdown', activeSlotId],
    queryFn: () => getEarningsBreakdown(activeSlotId!),
    enabled: !!activeSlotId,
  });

  const q = useQuery({
    queryKey: ['ledger', activeSlotId, page, PAGE_SIZE, filter],
    queryFn: () => getLedger({ slotId: activeSlotId ?? undefined, page, pageSize: PAGE_SIZE }),
    enabled: !!activeSlotId,
  });

  const rows = (q.data ?? []).filter((r) => filter === 'all' || r.direction === filter);

  const pagination = (
    <div className="flex items-center justify-between text-xs">
      <button
        disabled={page <= 1}
        onClick={() => setPage((p) => Math.max(1, p - 1))}
        className="text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
      >
        ← Prev
      </button>
      <span className="tabular-nums text-muted-foreground">Page {page}</span>
      <button
        disabled={(q.data?.length ?? 0) < PAGE_SIZE}
        onClick={() => setPage((p) => p + 1)}
        className="text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
      >
        Next →
      </button>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-h1">
            <TrendingUp className="h-6 w-6 text-muted-foreground" /> Earnings
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">Full ledger for this slot.</p>
        </div>
        <SegmentedToggle
          ariaLabel="Filter ledger entries"
          options={FILTERS}
          value={filter}
          onChange={(v) => setFilter(v)}
        />
      </div>

      {/* 4-up KPI summary above the full ledger (Omni Member App.html Earnings).
          An open CD/Half-CD debt adds a 5th tile so the "earned but garnished"
          math is visible right where the member looks for their money. */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard
          label="Available"
          value={fmtCurrency(walletQ.data?.available ?? '0')}
          icon={Wallet}
          emphasis="primary"
        />
        <KpiCard label="Pending" value={fmtCurrency(walletQ.data?.pending ?? '0')} icon={Clock} />
        <KpiCard
          label="Lifetime Earned"
          value={fmtCurrency(walletQ.data?.totalEarned ?? '0')}
          icon={TrendingUp}
        />
        <KpiCard
          label="Group PV"
          value={standingQ.data?.pv?.groupPv ?? '0'}
          hint={standingQ.data?.pv?.periodCode ?? undefined}
          icon={Network}
        />
        {walletQ.data?.debt && !walletQ.data.debt.settledAt ? (
          <KpiCard
            label="Kit Debt Remaining"
            value={fmtCurrency(walletQ.data.debt.debtRemaining)}
            hint={`${fmtCurrency(walletQ.data.debt.debtPaid)} of ${fmtCurrency(walletQ.data.debt.debtTotal)} paid`}
            icon={Clock}
          />
        ) : null}
      </div>

      {walletQ.data?.debt &&
      !walletQ.data.debt.settledAt &&
      meQ.data?.tenantPolicy?.walletTopupEnabled &&
      meQ.data?.tenantPolicy?.cdTopupSettlesDebt ? (
        <Button asChild variant="outline" size="sm" className="w-full sm:w-auto">
          <Link to="/wallet-topup">
            Settle this kit debt by topping up <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      ) : null}

      {earningsBreakdownQ.data ? (
        <EarningsBreakdownPanel
          entries={earningsBreakdownQ.data}
          columns={3}
          slotId={activeSlotId}
          kindLabels={meQ.data?.ledgerLabels}
          planLabels={meQ.data?.ledgerSourcePlanLabels}
          legacyOffset={walletQ.data?.legacyEarnedOffset}
        />
      ) : null}

      {q.isLoading ? (
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-48 w-full" />
          </CardContent>
        </Card>
      ) : (
        <StatementList
          entries={rows}
          kindLabels={meQ.data?.ledgerLabels}
          planLabels={meQ.data?.ledgerSourcePlanLabels}
          subtitle={activeSlotLabel ?? undefined}
          footer={pagination}
          emptyText={
            filter === 'C'
              ? 'No credits on this page.'
              : filter === 'D'
                ? 'No debits on this page.'
                : 'No entries on this page.'
          }
        />
      )}
    </div>
  );
}
